import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Grid, Box, CircularProgress, Alert, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, Select,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { testsApi } from '../api';

function GaugeCircle({ value, label }) {
  const clampedValue = Math.min(100, Math.max(0, value));
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress variant="determinate" value={clampedValue} size={100} thickness={5}
          color={clampedValue > 80 ? 'success' : clampedValue > 60 ? 'warning' : 'error'} />
        <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h5" fontWeight="bold">{Math.round(value)}%</Typography>
        </Box>
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>{label}</Typography>
    </Box>
  );
}

function Tests({ user }) {
  const [summary, setSummary] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTest, setSelectedTest] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sumRes, testRes] = await Promise.all([
        testsApi.summary(),
        testsApi.list(typeFilter, statusFilter),
      ]);
      setSummary(sumRes.data);
      setTests(testRes.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch test data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [typeFilter, statusFilter]);

  const runTests = async (type) => {
    setRunning(true);
    try {
      await testsApi.run(type || undefined);
      await fetchData();
    } catch {
      setError('Failed to run tests');
    } finally {
      setRunning(false);
    }
  };

  if (loading && !summary) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  const isAdmin = user?.role === 'admin';

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Testing & Coverage</Typography>
        {isAdmin && (
          <>
            <Button startIcon={<PlayArrowIcon />} variant="contained" onClick={() => runTests()} disabled={running}>
              {running ? 'Running...' : 'Run All Tests'}
            </Button>
            <Button size="small" onClick={() => runTests('unit')} disabled={running}>Unit</Button>
            <Button size="small" onClick={() => runTests('integration')} disabled={running}>Integration</Button>
            <Button size="small" onClick={() => runTests('e2e')} disabled={running}>E2E</Button>
            <Button size="small" onClick={() => runTests('ui')} disabled={running}>UI</Button>
            <Button size="small" onClick={() => runTests('security')} disabled={running}>Security</Button>
            <Button size="small" onClick={() => runTests('performance')} disabled={running}>Performance</Button>
            <Button startIcon={<DeleteSweepIcon />} color="warning" size="small" onClick={async () => { await testsApi.clearResults('failed'); fetchData(); }} disabled={running}>Clear Failed</Button>
          </>
        )}
        <Button startIcon={<RefreshIcon />} onClick={fetchData}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2 }}>
              <GaugeCircle value={summary.passRate} label="Pass Rate" />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2 }}>
              <GaugeCircle value={summary.averageCoverage} label="Avg Coverage" />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="success.main">{summary.passed}</Typography>
              <Typography variant="subtitle2">Passed</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="error.main">{summary.failed}</Typography>
              <Typography variant="subtitle2">Failed</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {summary?.byType?.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summary.byType.map(bt => (
            <Grid item xs={6} sm={3} key={bt.type}>
              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="subtitle2" textTransform="capitalize">{bt.type}</Typography>
                <Typography variant="body2">{bt.passed}/{bt.count} passed</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="unit">Unit</MenuItem>
            <MenuItem value="integration">Integration</MenuItem>
            <MenuItem value="e2e">E2E</MenuItem>
            <MenuItem value="ui">UI</MenuItem>
            <MenuItem value="security">Security</MenuItem>
            <MenuItem value="performance">Performance</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="passed">Passed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Coverage</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Run At</TableCell>
              <TableCell>Triggered By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests.map((t) => (
              <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedTest(t)}>
                <TableCell>{t.name}</TableCell>
                <TableCell><Chip label={t.type} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Chip label={t.status} size="small"
                    color={t.status === 'passed' ? 'success' : t.status === 'failed' ? 'error' : 'default'} />
                </TableCell>
                <TableCell>{t.coveragePercent != null ? `${t.coveragePercent}%` : '-'}</TableCell>
                <TableCell>{t.durationMs ? `${t.durationMs}ms` : '-'}</TableCell>
                <TableCell>{t.runAt ? new Date(t.runAt).toLocaleString() : '-'}</TableCell>
                <TableCell>{t.triggeredBy || '-'}</TableCell>
              </TableRow>
            ))}
            {tests.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                    No test results yet. Run tests to see results here.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Test Detail Dialog */}
      <Dialog open={!!selectedTest} onClose={() => setSelectedTest(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Test Detail: {selectedTest?.name}</DialogTitle>
        <DialogContent dividers>
          {selectedTest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label={selectedTest.type} variant="outlined" />
                <Chip label={selectedTest.status} color={selectedTest.status === 'passed' ? 'success' : 'error'} />
              </Box>
              <Typography variant="body2"><strong>Coverage:</strong> {selectedTest.coveragePercent != null ? `${selectedTest.coveragePercent}%` : 'N/A'}</Typography>
              <Typography variant="body2"><strong>Duration:</strong> {selectedTest.durationMs ? `${selectedTest.durationMs}ms` : 'N/A'}</Typography>
              <Typography variant="body2"><strong>Run At:</strong> {selectedTest.runAt ? new Date(selectedTest.runAt).toLocaleString() : 'N/A'}</Typography>
              <Typography variant="body2"><strong>Triggered By:</strong> {selectedTest.triggeredBy || 'N/A'}</Typography>
              {selectedTest.logOutput && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Log Output</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.900', color: 'grey.100', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                    {selectedTest.logOutput}
                  </Paper>
                </Box>
              )}
              {selectedTest.errorMessage && (
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>Error</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'error.dark', color: 'error.contrastText', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {selectedTest.errorMessage}
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTest(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Infrastructure Test Types</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following infrastructure checks are available. Click "Run All" above to execute them, or run them individually via the API.
          </Typography>
          {[
            { name: 'Docker.Socket', desc: 'Verifies the gateway can connect to the Docker daemon via unix socket.' },
            { name: 'DockerService.ListContainers', desc: 'Confirms container enumeration works end-to-end.' },
            { name: 'DNS.Resolution', desc: 'Checks that internal DNS can resolve the database service hostname.' },
            { name: 'CertService.Communication', desc: 'Pings the certificate service /health endpoint.' },
          ].map(item => (
            <Box key={item.name} sx={{ mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">{item.name}</Typography>
              <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
            </Box>
          ))}
        </Paper>
      </Box>
    </Container>
  );
}
export default Tests;
